import { tradeRequestApi } from 'apiConnector/quote/trade';
import * as apiErrorHelper from 'epic/apiErrorEpicHelper';
import { DocType } from 'epic/common-form/Trade';
import { thunksByWorkspaceAndDoc } from 'epic/streamingQuote/utils';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import { BackendError } from 'models/NextWorkflowError';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { EMPTY, merge, of } from 'rxjs';
import { catchError, filter, mergeMap, startWith, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import type { FunkyAction } from 'shared/middlewares/epic/epicMiddleware.factory';
import { commonStreamActions } from 'state/forms/actions/stream';
import { commonTradeAction } from 'state/forms/actions/trade';
import type { TradeExecReport } from 'state/quotes/trades/trades.model';
import type { EpicWithThunk } from 'state/state.common';
import { fatEventApiConnector } from 'utils/streaming/api';
import { tradeExecutionReport } from 'utils/streaming/messages';

import { throwOnTimeout, wrapError } from '../../utils/rxjs';

function handleTradeReport(
  tabId: string,
  quoteId: string,
  { status, errors, bookingSkipped }: TradeExecReport,
  when: Date,
): Observable<FunkyAction> {
  switch (status) {
    case 'FULLY_EXECUTED':
      return of(commonTradeAction.quoteTradeDone('dynamic', tabId, quoteId, status, when, bookingSkipped));
    case 'CANCELLED':
    case 'EXPIRED':
    case 'FAILED':
    case 'REJECTED':
      return of(commonTradeAction.quoteTradeRejected('dynamic', tabId, quoteId, errors!, when));
    default:
      return EMPTY;
  }
}
export function requestStreamingTradeEpic(): EpicWithThunk {
  return (
    action$,
    state$,
    { oldHttpClient, selectors: { getDynamicFormQuoteId, getDynamicTradeRequest }, getNow, getSgmeConfiguration },
  ) =>
    action$.pipe(
      ofType('STREAMING_TRADE_REQUESTED'),
      filter(a => a.workspace === 'dynamic'),
      withLatestFrom(state$),
      mergeMap(([{ tabId }, state]) => {
        const quoteId = getDynamicFormQuoteId(state, tabId)!;
        const tradeRequest = getDynamicTradeRequest(state, tabId);
        const tradeAckTimeoutMs = getSgmeConfiguration().tradeAckTimeoutMs;
        const tabRemoved$ = tabRemoved(action$, tabId, state$);
        const tradeResponse$ = tradeRequestApi(oldHttpClient, tradeAckTimeoutMs).requestTrade(tradeRequest);
        return tradeResponse$.pipe(
          takeUntil(tabRemoved$),
          mergeMap(response => {
            const documentThunks = Object.entries(response.documentation).map(([docType, documents]) =>
              of(
                thunksByWorkspaceAndDoc.dynamic[docType as DocType]?.(
                  tabId,
                  quoteId,
                  {
                    quoteId,
                    requestId: quoteId,
                    listResources: documents.map(({ url, ...others }) => ({ uri: url, ...others })),
                  },
                  true,
                ),
              ),
            );
            return merge(
              ...documentThunks,
              of(commonStreamActions.quoteStreamingTradeDone('dynamic', tabId, getNow(), response)),
            );
          }),
          catchError(error => {
            if (apiErrorHelper.isHttpError(error) && error.status === 400) {
              const errors = error.response as BackendError;
              return of(commonTradeAction.quoteTradeRejected('dynamic', tabId, quoteId, errors.rejections, getNow()));
            }
            return handleError(tabId, quoteId, error);
          }),
        );
      }),
    );
}
export function requestTradeEpic(appScheduler?: SchedulerLike): EpicWithThunk {
  return (
    action$,
    state$,
    {
      oldHttpClient,
      selectors: { getDynamicFormQuoteId, getDynamicTradeRequest },
      getSgmeConfiguration,
      streaming,
      getNow,
    },
  ) =>
    action$.pipe(
      ofType('QUOTE_TRADE_REQUESTED'),
      filter(a => a.workspace === 'dynamic'),
      withLatestFrom(state$),
      mergeMap(([{ tabId }, state]) => {
        const quoteId = getDynamicFormQuoteId(state, tabId)!;
        const tradeRequest = getDynamicTradeRequest(state, tabId);
        const tabRemoved$ = tabRemoved(action$, tabId, state$);
        const tradeAckTimeoutMs = getSgmeConfiguration().tradeAckTimeoutMs;
        const tradeExecReportTimeoutMs = getSgmeConfiguration().tradeExecReportTimeoutMs;

        const tradeResponse$ = tradeRequestApi(oldHttpClient, tradeAckTimeoutMs).requestTrade(tradeRequest);
        return tradeResponse$.pipe(
          takeUntil(tabRemoved$),
          mergeMap(() => {
            const tradeFailed$ = action$.pipe(
              ofType('QUOTE_TRADE_FAILED'),
              filter(a => a.workspace === 'dynamic' && a.tabId === tabId),
            );

            const completionAction$ = tradeExecutionReport(streaming).pipe(
              takeUntil(tradeFailed$),
              filter(r => r.quoteId === quoteId),
              mergeMap(lightEvent =>
                fatEventApiConnector(oldHttpClient)
                  .getFatEvent<TradeExecReport>(lightEvent)
                  .pipe(
                    mergeMap(report => handleTradeReport(tabId, quoteId, report, getNow())),
                    wrapError(
                      error =>
                        new apiErrorHelper.EpicApiError('trade.errorMessages.executionReportTechnicalError', error),
                      appScheduler,
                    ),
                  ),
              ),
              takeUntil(tabRemoved$),
              take(1),
              throwOnTimeout(
                tradeExecReportTimeoutMs,
                new apiErrorHelper.TimeoutError(tradeExecReportTimeoutMs, 'trade.errorMessages.executionReportTimeout'),
                appScheduler,
              ),
              startWith(commonTradeAction.quoteTradeSent('dynamic', tabId, quoteId)),
            );

            return completionAction$;
          }),
          catchError(error => {
            return handleError(tabId, quoteId, error);
          }),
        );
      }),
    );
}

const handleError = (tabId: string, quoteId: string, error: Error) => {
  if (apiErrorHelper.isHttpError(error) && error.status === 400) {
    return of(commonTradeAction.quoteTradeRequestRejected('dynamic', tabId, quoteId, error.response));
  }
  return of(
    commonTradeAction.quoteTradeFailed(
      'dynamic',
      tabId,
      quoteId,
      apiErrorHelper.toApiErrorMessage(error, {
        timeoutErrorMessage: 'trade.errorMessages.requestTimeout',
      }),
      error,
    ),
  );
};
